/**
 * ListView - displays tasks in a list/table format
 */

import { useState, useMemo } from 'react';
import { useBoardStore } from '@stores/boardStore';
import type { Task } from '@services/api';

interface ListViewProps {
  onTaskClick: (taskId: string) => void;
  searchQuery?: string;
  filterPriority?: string;
  filterColumnId?: string;
}

type SortField = 'title' | 'columnId' | 'priority' | 'dueDate' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const priorityOrder: Record<string, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
  none: 4,
};

const priorityColors: Record<string, string> = {
  urgent: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
  high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200',
  medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
  low: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
  none: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200',
};

export default function ListView({
  onTaskClick,
  searchQuery: externalSearchQuery = '',
  filterPriority: externalFilterPriority = '',
  filterColumnId: externalFilterColumnId = '',
}: ListViewProps) {
  const { currentBoard, tasks } = useBoardStore();

  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [localFilterPriority, setLocalFilterPriority] = useState<string>('');
  const [localFilterColumnId, setLocalFilterColumnId] = useState<string>('');
  const [localSearchQuery, setLocalSearchQuery] = useState('');

  // Use external filters if provided, otherwise use local
  const searchQuery = externalSearchQuery || localSearchQuery;
  const filterPriority = externalFilterPriority || localFilterPriority;
  const filterColumnId = externalFilterColumnId || localFilterColumnId;

  // Get all tasks as array
  const allTasks = useMemo(() => {
    const taskList: Task[] = [];
    Object.values(tasks).forEach((task) => {
      if (task) taskList.push(task);
    });
    return taskList;
  }, [tasks]);

  // Filter tasks
  const filteredTasks = useMemo(() => {
    return allTasks.filter((task) => {
      if (filterPriority && task.priority !== filterPriority) return false;
      if (filterColumnId && task.columnId !== filterColumnId) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          task.title.toLowerCase().includes(query) ||
          task.taskKey.toLowerCase().includes(query) ||
          task.description?.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [allTasks, filterPriority, filterColumnId, searchQuery]);

  // Sort tasks
  const sortedTasks = useMemo(() => {
    return [...filteredTasks].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'columnId':
          comparison = a.columnId.localeCompare(b.columnId);
          break;
        case 'priority':
          comparison =
            (priorityOrder[a.priority] ?? 4) - (priorityOrder[b.priority] ?? 4);
          break;
        case 'dueDate': {
          const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
          const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
          comparison = dateA - dateB;
          break;
        }
        case 'createdAt':
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredTasks, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getColumnName = (columnId: string): string => {
    const column = currentBoard?.columns.find((c) => c.id === columnId);
    return column?.name ?? 'Unknown';
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-neutral-800">
      {/* Filters */}
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center gap-4 flex-wrap">
        {/* Only show local search if no external search is provided */}
        {!externalSearchQuery && (
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search tasks..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md text-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        )}
        {/* Only show local filters if no external filters provided */}
        {!externalFilterPriority && (
          <select
            value={localFilterPriority}
            onChange={(e) => setLocalFilterPriority(e.target.value)}
            className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md text-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
          >
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="none">None</option>
          </select>
        )}
        {!externalFilterColumnId && (
          <select
            value={localFilterColumnId}
            onChange={(e) => setLocalFilterColumnId(e.target.value)}
            className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md text-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
          >
            <option value="">All Columns</option>
            {currentBoard?.columns.map((col) => (
              <option key={col.id} value={col.id}>
                {col.name}
              </option>
            ))}
          </select>
        )}
        <span className="text-sm text-neutral-500 dark:text-neutral-400">
          {sortedTasks.length} task{sortedTasks.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
          <thead className="bg-neutral-50 dark:bg-neutral-900 sticky top-0">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => handleSort('title')}
              >
                <div className="flex items-center gap-2">
                  Task
                  <SortIcon field="title" />
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => handleSort('columnId')}
              >
                <div className="flex items-center gap-2">
                  Column
                  <SortIcon field="columnId" />
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => handleSort('priority')}
              >
                <div className="flex items-center gap-2">
                  Priority
                  <SortIcon field="priority" />
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => handleSort('dueDate')}
              >
                <div className="flex items-center gap-2">
                  Due Date
                  <SortIcon field="dueDate" />
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
              >
                Labels
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
            {sortedTasks.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-neutral-500 dark:text-neutral-400">
                  No tasks found
                </td>
              </tr>
            ) : (
              sortedTasks.map((task) => (
                <tr
                  key={task.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open task ${task.title}`}
                  onClick={() => onTaskClick(task.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onTaskClick(task.id);
                    }
                  }}
                  className="hover:bg-neutral-50 dark:hover:bg-neutral-700 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-neutral-900 dark:text-white">
                          {task.title}
                        </div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">{task.taskKey}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
                      {getColumnName(task.columnId)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                        priorityColors[task.priority] ?? priorityColors.none
                      }`}
                    >
                      {task.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                    {task.dueDate
                      ? new Date(task.dueDate).toLocaleDateString()
                      : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1 flex-wrap">
                      {task.labels.slice(0, 3).map((label) => (
                        <span
                          key={label.id}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: `${label.color}20`,
                            color: label.color,
                          }}
                        >
                          {label.name}
                        </span>
                      ))}
                      {task.labels.length > 3 && (
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">
                          +{task.labels.length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
